import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ListPageTopBar } from "@/components/layout/dashboard-layout";
import { useTable } from "@/hooks/useTable";
import { ListFooter } from "@/components/shared/list-footer";

import { ErrorBoundary } from "react-error-boundary";
import { RecordNotFound } from "@/components/shared/record-not-found";
import { TableColumnsHandle } from "@/components/shared/table-columns-handle";
import {
  ArrowLeftIcon,
  CircleHelp,
  Columns3Icon,
  FilterIcon,
  X,
} from "lucide-react";
import { useMutations } from "./utils/react-hook/use-mutations";
import {
  PasskeyPopup,
  PasswordResetForm,
  ReactForm,
  ReactTable,
  ReactTableSkelton,
} from "./utils";
import { Fragment } from "react/jsx-runtime";
import ErrorFallback from "@/components/shared/error-fallback";
import { ConditionRenderer } from "@/components/shared/condition-renderer";
import { EmailListenerDoc } from "./utils/email-listener-doc";
import { CustomErrorPopup } from "@/components/shared/custom-error-popup";
import { useUserInfo } from "@/global-data/use-user-info";
import { SelectEmailProvider } from "./utils/select-email-provider/select-email-provider";

export const EmailListener = () => {
  const {
    listData,
    onPageChange,
    onPerPageChange,
    paginationData,
    permission,
    onAction,
    activeRecordData,
    onFormSubmit,
    onFormOpenChange,
    formPopupOpen,
    skeltonLoading,
    tableColumn,
    setTableColumn,
    filter,
    setFilter,
    filterRequest,
    clearFilter,
    isFormReset,
    setIsFormReset,
    passKeyPopup,
    setPassKeyPopup,
    inlineUpdate,
    errorMessage,
    closeErrorMessage,
    emailProvider,
    setEmailProvider,
    aksPopup,
    setAksPopup,
  } = useMutations();

  const { tableHeader, table, tableKeys, onResize, onSorting } = useTable({
    data: listData,
    view: [
      "email_id",
      "enable",
      "read_body_content",
      "read_attachments",
      "modified_by",
    ],
    verifyKey: "email-listener",
    visible: tableColumn,
    avoid: [""],
  });

  const { activePermission } = useUserInfo();

  return (
    <Fragment>
      {formPopupOpen ? (
        <div className="flex flex-col gap-5">
          <ListPageTopBar
            title={
              activeRecordData
                ? "Update Email Listener"
                : "Create Email Listener"
            }
          >
            <EmailListenerDoc>
              <Button variant={"action"}>
                How to set up an app password ?
              </Button>
            </EmailListenerDoc>
            <Button
              className="hidden"
              // variant={"action"}
              variant={"outline"}
              onClick={() => setPassKeyPopup(true)}
            >
              <CircleHelp /> How to create passkey
            </Button>
            <Button
              variant={"action"}
              onClick={() => {
                onFormOpenChange(false);
                closeErrorMessage();
              }}
            >
              <ArrowLeftIcon /> Back
            </Button>
          </ListPageTopBar>
          <Card className="pt-6">
            <>
              <ConditionRenderer condition={isFormReset}>
                <PasswordResetForm
                  formData={activeRecordData}
                  onFormSubmit={onFormSubmit}
                  onOpenChange={onFormOpenChange}
                  open={formPopupOpen}
                  setIsFormReset={setIsFormReset}
                  isFormReset={isFormReset}
                />
                <ReactForm
                  formData={activeRecordData}
                  onFormSubmit={onFormSubmit}
                  onOpenChange={onFormOpenChange}
                  open={formPopupOpen}
                  setIsFormReset={setIsFormReset}
                  isFormReset={isFormReset}
                  emailProvider={emailProvider}
                />
              </ConditionRenderer>
            </>
          </Card>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <ListPageTopBar title="Email Listener">
            <EmailListenerDoc>
              <Button variant={"action"}>
                How to set up an app password ?
              </Button>
            </EmailListenerDoc>
            <TableColumnsHandle
              tableKeys={tableKeys}
              tableColumn={tableColumn}
              setTableColumn={setTableColumn}
              onSorting={onSorting}
              labels={{
                email_id: "Email ID",
                enable: "Status",
                modified_by: "Modified By",
              }}
            >
              <Columns3Icon /> Column
            </TableColumnsHandle>

            <ConditionRenderer condition={Object.keys(filter).length !== 0}>
              <>
                <Button variant={"action"} onClick={filterRequest}>
                  <FilterIcon /> Filter
                </Button>
                <Button variant={"action"} onClick={clearFilter}>
                  <X /> Clear Filter
                </Button>
              </>
            </ConditionRenderer>
            {activePermission?.create ? (
              <Button variant={"action"} onClick={() => setAksPopup(true)}>
                + Create
              </Button>
            ) : null}
          </ListPageTopBar>

          {skeltonLoading ? (
            <ReactTableSkelton />
          ) : (
            <Card className="overflow-hidden">
              <CardContent className="max-w-[100%] p-0 overflow-auto scrollbar h-auto min-h-[350px] md:h-[calc(100dvh-340px)]">
                <ErrorBoundary
                  fallbackRender={({ error, resetErrorBoundary }) => (
                    <ErrorFallback
                      error={error}
                      errorInfo={error.stack || ""}
                      resetErrorBoundary={resetErrorBoundary}
                    />
                  )}
                >
                  <ReactTable
                    tableHeader={tableHeader}
                    onResize={onResize}
                    table={table}
                    permission={permission}
                    onAction={onAction}
                    listData={listData}
                    filter={filter}
                    setFilter={setFilter}
                    filterRequest={filterRequest}
                    inlineUpdate={inlineUpdate}
                    activePermission={activePermission}
                  />
                  {table.length === 0 ? <RecordNotFound /> : null}
                </ErrorBoundary>
              </CardContent>
              <ErrorBoundary
                fallbackRender={({ error, resetErrorBoundary }) => (
                  <ErrorFallback
                    error={error}
                    errorInfo={error.stack || ""}
                    resetErrorBoundary={resetErrorBoundary}
                  />
                )}
              >
                <hr />
                <ListFooter
                  onPageChange={onPageChange}
                  onPerPageChange={onPerPageChange}
                  page={paginationData.page}
                  perPage={paginationData.perPage}
                  totalItem={paginationData.totalItem}
                  className="px-5 py-5"
                  isEmpty={table.length === 0}
                />
              </ErrorBoundary>
            </Card>
          )}
        </div>
      )}
      <PasskeyPopup open={passKeyPopup} setOpen={setPassKeyPopup} />
      <CustomErrorPopup
        title={errorMessage?.title}
        description={errorMessage?.message}
        open={errorMessage?.open}
        setOpen={closeErrorMessage}
      />
      <SelectEmailProvider
        setEmailProvider={setEmailProvider}
        open={aksPopup}
        setOpen={setAksPopup}
        setFormPopupOpen={onFormOpenChange}
      />
    </Fragment>
  );
};
